import { type ComponentProps, useEffect, useState } from 'react'
import { AnimatePresence, type motion } from 'motion/react'

export type Direction = 'prev' | 'next'

const animation = {
	center: {
		opacity: 1,
		x: 0,
		zIndex: 1,
	},
	enter: (direction: string) => ({
		opacity: 0,
		x: direction === 'next' ? 500 : -500,
		zIndex: 0,
	}),
	exit: (direction: string) => ({
		opacity: 0,
		x: direction === 'next' ? -500 : 500,
		zIndex: 0,
	}),
}

const framerDivConfig = {
	animate: 'center',
	initial: 'enter',
	transition: {
		opacity: { duration: 0.2 },
		x: { damping: 30, stiffness: 300, type: 'spring' as const },
	},
	variants: animation,
}

export const Stepper = <T extends string>({
	steps,
	defaultActiveStep,
	onStepChange,
	render,
}: {
	steps: readonly T[]
	defaultActiveStep?: T
	onStepChange?: (step: T) => void
	render: (props: {
		framerConfig: ComponentProps<typeof motion.div>
		activeStep: T
		goToStep: (step: T) => void
	}) => React.ReactNode
}) => {
	const [direction, setDirection] = useState<Direction>('next')
	const [activeStep, setActiveStep] = useState<T>(defaultActiveStep || steps[0])

	const goToStep = (step: T) => {
		if (activeStep === step) return

		const activeStepIndex = steps.indexOf(activeStep)
		const stepIndex = steps.indexOf(step)

		if (stepIndex > activeStepIndex) {
			setDirection('next')
		} else {
			setDirection('prev')
		}

		setActiveStep(step)
		onStepChange?.(step)
	}

	useEffect(() => {
		if (defaultActiveStep) {
			setActiveStep(defaultActiveStep)
		}
	}, [defaultActiveStep])

	return (
		<div className='relative'>
			<div className='relative'>
				<AnimatePresence
					custom={direction}
					initial={false}
					mode='wait'
				>
					{render({
						activeStep,
						framerConfig: { ...framerDivConfig, custom: direction },
						goToStep,
					})}
				</AnimatePresence>
			</div>
		</div>
	)
}
